import { useEffect, useState, useRef } from "react";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import HeadingComponent from "@/components/BoardPack/components/shared/heading";
import DottedLineBarChart from "@/components/BoardPack/components/charts/DottedLineBarChart";

const STATIC_RESPONSE = {
  success: true,
  meta: { year: 2026, month: "Apr", measure: 4 },
  data: {
    headerYears: ["2018","2019","2020","2022","2023","2024","2025","2026","2027","2028","2029","2030"],
    headerSources: ["Actual","Actual","Actual","Actual","Actual","Actual","Actual","MTP","MTP","MTP","MTP","MTP"],
    rows: [
      {
        label: "Total Revenue",
        values: ["-","-","4,276,534","36,514,209","40,112,929","43,266,200","44,452,432","43,764,819","44,640,115","45,532,917","46,443,576","47,372,447"],
        isPercent: false,
      },
      {
        label: "EBITDA",
        values: ["-","-","589,621","12,440,769","13,397,224","13,944,446","13,588,694","13,727,481","14,002,031","14,282,071","14,567,713","14,859,067"],
        isPercent: false,
      },
      {
        label: "OPERATING PROFIT/(LOSS)",
        values: ["-","-","371,129","9,566,207","10,594,741","7,400,987","7,336,896","8,294,667","8,569,217","8,849,258","9,134,899","9,426,253"],
        isPercent: false,
      },
      {
        label: "EBITDA Margin",
        values: ["-","-","13.79%","34.07%","33.40%","32.23%","30.57%","31.37%","31.37%","31.37%","31.37%","31.37%"],
        isPercent: true,
      },
      {
        label: "NET PROFIT MARGIN",
        values: ["-","-","8.68%","26.20%","26.41%","17.11%","16.51%","18.95%","19.20%","19.43%","19.67%","19.90%"],
        isPercent: true,
      },
    ],
  },
  is_cfo: true,
  is_ai: true,
  lines: [
    "- Sharjah Airport (2026): Total Revenue MTP: 43.8M vs Actual 2025: 44.5M — Decline",
    "- EBITDA MTP: 13.7M vs Actual 2025: 13.6M — Slight Increase",
    "- Operating Profit (LOSS): MTP: 8.3M vs Actual 2025: 7.3M — Growth",
    "- EBITDA Margin MTP: 31.4% vs Actual 2025: 30.6% — Slight Increase",
    "- Net Profit Margin MTP: 18.9% vs Actual 2025: 16.5% — Growth",
    "- Operating Profit (LOSS) Improvement from 2023 peak — Decline",
    "- Net Profit Margin below 2023 peak — Decline",
  ],
};

// ─── Data Transformer ───────────────────────────────────────────────────────
function transformApiResponse(apiData) {
  const d = apiData?.data ?? {};
  const labels = d.headerYears || [];
  const getRow = (name) =>
    d.rows?.find((r) => r.label === name)?.values || [];

  const parseNumber = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    if (typeof v === "string") {
      const isNegative = v.trim().startsWith("(");
      const cleaned = v.replace(/[(),\s]/g, "").replace("%", "");
      const num = Number(cleaned);
      return isNaN(num) ? 0 : isNegative ? -num : num;
    }
    return typeof v === "number" ? v : 0;
  };

  return {
    labels,
    totalRevenue: getRow("Total Revenue").map(parseNumber),
    ebitda: getRow("EBITDA").map(parseNumber),
    opProfit: getRow("OPERATING PROFIT/(LOSS)").map(parseNumber),
    ebitdaMargin: getRow("EBITDA Margin").map(parseNumber),
    netProfitMargin: getRow("NET PROFIT MARGIN").map(parseNumber),
  };
}

// ─── Helper to resolve placeholders ─────────────────────────────────────────
function resolveString(str, runtimeValues = {}) {
  if (typeof str !== "string") return "";
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    runtimeValues[k] !== undefined ? runtimeValues[k] : `{{${k}}}`
  );
}

export default function PageOneChartContainer({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  config = {},
  isExporting = false,
  cfoComment = "",
  onCfoCommentChange,
}) {
  const hasComment = cfoComment.trim() !== "";
  const [chartData, setChartData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const runtimeValues = { year, month, contract };
  const is_cfo = page?.params?.is_cfo ? true : false;

  const chartTitle = resolveString(
    page?.in_chart_content_line_1 || page?.title || "",
    runtimeValues
  );

  useEffect(() => {
    processData();
  }, []);

  const processData = () => {
    try {
      setLoading(true);
      setError(null);

      const response = STATIC_RESPONSE;

      if (response) {
        setChartData(transformApiResponse(response));
        setSummary({ lines: response?.lines || [] });
      } else {
        setError("No data received from server.");
      }
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to load chart data.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={processData} />;

  return (
    <div className="w-full min-h-screen py-4 md:py-8 px-2 md:px-4">
      <div
        className="w-full mx-auto px-3 md:px-6 lg:px-8 py-4 md:py-8 rounded-lg shadow-lg"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Title & Subtitle */}
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0rem 2rem",
            margin: "-2rem -2rem 2rem -2rem",
          }}
        >
          {page?.title && (
            <HeadingComponent variant={"main"}>
              {resolveString(page.title, runtimeValues)}
            </HeadingComponent>
          )}
          {page?.subtitle && (
            <HeadingComponent variant={"sub"}>
              {resolveString(page.subtitle, runtimeValues)}
            </HeadingComponent>
          )}
        </div>

        {/* Chart */}
        <DottedLineBarChart
          labels={chartData.labels}
          totalRevenue={chartData.totalRevenue}
          ebitda={chartData.ebitda}
          opProfit={chartData.opProfit}
          ebitdaMargin={chartData.ebitdaMargin}
          netProfitMargin={chartData.netProfitMargin}
          title={chartTitle}
          isCfo={is_cfo}
        />

        {/* AI Summary + CFO Comments */}
        {is_cfo && summary?.lines.length > 0 && (
          <>
            <div className="mt-1 flex items-start gap-4 p-4">
              <div className="flex-1 p-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                  <h3 className="text-sm font-semibold">AI Summary</h3>
                </div>
                {summary?.lines && summary.lines.length > 0 ? (
                  <ul className="space-y-1">
                    {summary.lines.map((line, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-[12px] font-medium">{line.replace(/^\s*-\s*/, "")}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500">No AI summary available</p>
                )}
              </div>

              {(!isExporting || (isExporting && hasComment)) && (
                <div className="flex-1 p-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                    <h3 className="text-sm font-semibold">CFO Comments</h3>
                  </div>
                  {!isExporting ? (
                    <textarea
                      value={cfoComment}
                      onChange={(e) => onCfoCommentChange?.(e.target.value)}
                      placeholder="Enter comments here..."
                      className="w-full h-32 p-3 border border-gray-300 rounded-md text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  ) : hasComment ? (
                    <p className="text-xs whitespace-pre-wrap break-words">{cfoComment}</p>
                  ) : null}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}